/**
 * @description
 * Component for displaying deployment health history with charts and trends.
 * Shows historical health data with visual indicators and performance metrics.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus, Activity, Clock } from 'lucide-react';

interface HealthHistoryProps {
  history: Array<{
    timestamp: string;
    overall: 'healthy' | 'degraded' | 'unhealthy';
    worker: {
      status: 'healthy' | 'degraded' | 'unhealthy';
      responseTime?: number;
    };
    database: {
      status: 'healthy' | 'degraded' | 'unhealthy';
      responseTime?: number;
    };
    domain: {
      status: 'healthy' | 'degraded' | 'unhealthy';
      responseTime?: number;
    };
  }>;
  className?: string;
}

export function DeploymentHealthHistory({ history, className = '' }: HealthHistoryProps) {
  const getStatusColor = (status: 'healthy' | 'degraded' | 'unhealthy') => {
    switch (status) {
      case 'healthy':
        return 'bg-green-500';
      case 'degraded':
        return 'bg-yellow-500';
      case 'unhealthy':
        return 'bg-red-500';
    }
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (current > previous) return <TrendingUp className="w-3 h-3 text-red-500" />;
    if (current < previous) return <TrendingDown className="w-3 h-3 text-green-500" />;
    return <Minus className="w-3 h-3 text-gray-500" />;
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatResponseTime = (time?: number) => {
    if (!time) return 'N/A';
    return `${time}ms`;
  };

  // Calculate trends
  const calculateTrends = () => {
    if (history.length < 2) return null;

    const latest = history[0];
    const previous = history[1];

    return {
      worker: latest.worker.responseTime && previous.worker.responseTime 
        ? latest.worker.responseTime - previous.worker.responseTime 
        : 0,
      database: latest.database.responseTime && previous.database.responseTime 
        ? latest.database.responseTime - previous.database.responseTime 
        : 0,
      domain: latest.domain.responseTime && previous.domain.responseTime 
        ? latest.domain.responseTime - previous.domain.responseTime 
        : 0,
    };
  };

  const trends = calculateTrends();

  // Calculate uptime percentage
  const calculateUptime = () => {
    const healthyCount = history.filter(h => h.overall === 'healthy').length;
    return history.length > 0 ? Math.round((healthyCount / history.length) * 100) : 0;
  };

  const uptimePercentage = calculateUptime();

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Activity className="w-4 h-4 text-blue-500" />
            <h3 className="font-semibold">Uptime</h3>
          </div>
          <p className="text-2xl font-bold text-blue-700">{uptimePercentage}%</p>
          <p className="text-sm text-gray-600">Last {history.length} checks</p>
        </div>

        <div className="p-4 bg-green-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-green-500" />
            <h3 className="font-semibold">Avg Response</h3>
          </div>
          <p className="text-2xl font-bold text-green-700">
            {history.length > 0 
              ? Math.round(
                  history.reduce((sum, h) => 
                    sum + (h.worker.responseTime || 0) + (h.database.responseTime || 0) + (h.domain.responseTime || 0), 0
                  ) / (history.length * 3)
                )
              : 0
            }ms
          </p>
          <p className="text-sm text-gray-600">All components</p>
        </div>

        <div className="p-4 bg-purple-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-purple-500" />
            <h3 className="font-semibold">Last Check</h3>
          </div>
          <p className="text-lg font-bold text-purple-700">
            {history.length > 0 ? formatTimestamp(history[0].timestamp) : 'No data'}
          </p>
          <p className="text-sm text-gray-600">Health monitoring</p>
        </div>
      </div>

      {/* Response Time Trends */}
      {trends && (
        <div className="p-4 border rounded-lg">
          <h3 className="font-semibold mb-4">Response Time Trends</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <span className="font-medium">Worker</span>
              <div className="flex items-center gap-2">
                <span className="text-sm">
                  {history[0]?.worker.responseTime ? `${history[0].worker.responseTime}ms` : 'N/A'}
                </span>
                {getTrendIcon(history[0]?.worker.responseTime || 0, history[1]?.worker.responseTime || 0)}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <span className="font-medium">Database</span>
              <div className="flex items-center gap-2">
                <span className="text-sm">
                  {history[0]?.database.responseTime ? `${history[0].database.responseTime}ms` : 'N/A'}
                </span>
                {getTrendIcon(history[0]?.database.responseTime || 0, history[1]?.database.responseTime || 0)}
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
              <span className="font-medium">Domain</span>
              <div className="flex items-center gap-2">
                <span className="text-sm">
                  {history[0]?.domain.responseTime ? `${history[0].domain.responseTime}ms` : 'N/A'}
                </span>
                {getTrendIcon(history[0]?.domain.responseTime || 0, history[1]?.domain.responseTime || 0)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Health Timeline */}
      <div className="p-4 border rounded-lg">
        <h3 className="font-semibold mb-4">Health Timeline</h3>
        <div className="space-y-2">
          {history.slice(0, 10).map((entry, index) => (
            <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded">
              <div className={`w-3 h-3 rounded-full ${getStatusColor(entry.overall)}`} />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize">{entry.overall}</span>
                  <span className="text-sm text-gray-500">{formatTimestamp(entry.timestamp)}</span>
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                  <span>Worker: {formatResponseTime(entry.worker.responseTime)}</span>
                  <span>DB: {formatResponseTime(entry.database.responseTime)}</span>
                  <span>Domain: {formatResponseTime(entry.domain.responseTime)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {history.length > 10 && (
          <p className="text-sm text-gray-500 mt-2 text-center">
            Showing last 10 checks of {history.length} total
          </p>
        )}
      </div>
    </div>
  );
}

export default DeploymentHealthHistory;
